/* eslint-disable no-restricted-globals */
"use client"

import { useState, useEffect, useRef } from "react"
import "./Academic.css"
import { useTranslation } from "react-i18next"

const Academics = () => {
  // State variables
  const { t } = useTranslation();
  const skillTabsRef = useRef(null)
  const languageTabsRef = useRef(null)
  const [selectedFilter, setSelectedFilter] = useState("Skill")
  const [activeTab, setActiveTab] = useState("N5")
  const [searchTerm, setSearchTerm] = useState("")
  // Add state for active skill tab
  const [activeSkillTab, setActiveSkillTab] = useState("Food Service")
  // State for counting animation
  const [countingStarted, setCountingStarted] = useState(false)

  // Images for the page
  const images = {
    // Course images
    jpt: "/images/academics/jpt.gif",
    jlpt: "/images/academics/jlpt.gif",
    foodService: "/images/academics/foodservice.gif",
    agriculture: "/images/academics/agri.gif",
    elderCare: "/images/academics/eldercare.gif",
    cleaning: "/images/academics/cleaning.gif",
    airport: "/images/academics/airport.gif",

    // Icon images
    icon1: "/images/academics/icon1.png",
    icon2: "/images/academics/icon2.png",
    icon3: "/images/academics/icon3.png",
    icon4: "/images/academics/icon4.png",

    // Course detail image
    courseDetail: "/images/academics/course.jpg",

    // Trust section images
    japanSuccess: "/images/academics/slide1.jpg",
    certificate: "/images/academics/validate.png",
    handshake: "/images/academics/industry.png",
    support: "/images/academics/passport.png",
    curriculum: "/images/academics/workplace.png",
    verified: "/images/academics/validate.png",
    award: "/images/academics/award.png",
    guarantee: "/images/academics/guarantee.png",
  }

  // Event handlers
  const handleTabClick = (tab) => {
    setActiveTab(tab)
  }

  // Handle skill tab click
  const handleSkillTabClick = (tab) => {
    setActiveSkillTab(tab)
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  // Update the search button to use an icon instead of text
  const handleSearchSubmit = (e) => {
    e.preventDefault()
    // Search is already being applied as the user types
  }

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId)
    if (section) {
      section.scrollIntoView({ behavior: "smooth" })
    }
  }

  // Scroll tabs left/right
  /*
  const scrollTabs = (direction, tabsRef) => {
    const container = tabsRef.current
    if (!container) return

    // Calculate visible width
    const containerWidth = container.offsetWidth

    // Determine number of visible tabs based on screen width
    let visibleTabs = 3 // Default for mobile
    if (window.innerWidth >= 1200) {
      visibleTabs = 5 // Laptop
    } else if (window.innerWidth >= 769) {
      visibleTabs = 4 // Tablet
    }

    // Calculate scroll amount based on container width and visible tabs
    const scrollAmount = containerWidth / visibleTabs

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    })
  }
    */

  // Course data
  const courses = [
    // Language courses
    {
      id: 1,
      title: "N5 Course",
      type: "Language",
      description: "Build a solid foundation in basic Japanese language skills",
      image: images.jpt,
      duration: "2 months",
      level: "Beginner",
      features: ["Basic vocabulary", "Simple grammar", "Everyday expressions"],
      targetSection: "language-tabs-section",
      targetTab: "N5",
    },
    {
      id: 2,
      title: "N4 Course",
      type: "Language",
      description: "Advance your Japanese skills for basic communication",
      image: images.jlpt,
      duration: "3 months",
      level: "Basic to Intermediate",
      features: ["Expanded vocabulary", "Intermediate grammar", "Reading comprehension"],
      targetSection: "language-tabs-section",
      targetTab: "N4",
    },
    {
      id: 3,
      title: "JLPT Course",
      type: "Language",
      description: "Advance in better educational or career opportunities",
      image: images.jlpt,
      duration: "6 months",
      level: "All levels (N5-N1)",
      features: ["Exam strategies", "Grammar mastery", "Vocabulary building"],
      targetSection: "language-tabs-section",
      targetTab: "JLPT",
    },
    {
      id: 4,
      title: "JFT Course",
      type: "Language",
      description: "Start communicating in mday-to-day situations at ease",
      image: images.jpt,
      duration: "3 months",
      level: "Beginner to Intermediate",
      features: ["Practical conversation", "Reading skills", "Writing practice"],
      targetSection: "language-tabs-section",
      targetTab: "JFT",
    },

    // Skill courses
    {
      id: 5,
      title: "Food Service",
      type: "Skill",
      description: "Learn food preparation, service, and hygiene for restaurant work",
      image: images.foodService,
      duration: "2 months",
      level: "Beginner",
      features: ["Food safety", "Customer service", "Basic cooking"],
      targetSection: "detailed-skill-courses",
      targetTab: "Food Service",
    },
    {
      id: 6,
      title: "Agriculture",
      type: "Skill",
      description: "Learn agricultural techniques required for Japan employment",
      image: images.agriculture,
      duration: "2 months",
      level: "Beginner",
      features: ["Crop management", "Equipment handling", "Safety protocols"],
      targetSection: "detailed-skill-courses",
      targetTab: "Agriculture",
    },
    {
      id: 7,
      title: "Care Giver",
      type: "Skill",
      description: "Skills need for quality care for elderly, disabled individuals",
      image: images.elderCare,
      duration: "3 months",
      level: "Intermediate",
      features: ["Patient handling", "Medical terminology", "Emergency response"],
      targetSection: "detailed-skill-courses",
      targetTab: "Care Giving",
    },
    {
      id: 8,
      title: "Building Cleaning",
      type: "Skill",
      description: "Master professional cleaning techniques for commercial buildings",
      image: images.cleaning,
      duration: "1 month",
      level: "Beginner",
      features: ["Industrial cleaning", "Equipment usage", "Safety standards"],
      targetSection: "detailed-skill-courses",
      targetTab: "Building Cleaning",
    },
    {
      id: 9,
      title: "Ground Handling",
      type: "Skill",
      description: "Train for airport ground operations and passenger services",
      image: images.airport,
      duration: "3 months",
      level: "Intermediate",
      features: ["Baggage handling", "Aircraft servicing", "Safety procedures"],
      targetSection: "detailed-skill-courses",
      targetTab: "Ground Handling",
    },
  ]

  // Filter courses based on selected filter and search term
  const filteredCourses = courses.filter((course) => {
    const matchesType = course.type === selectedFilter
    const matchesSearch =
      searchTerm === "" ||
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.level.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesType && matchesSearch
  })

  // Language tab content
  const languageTabContent = {
    N5: {
      description:
        "Our N5 Language Course helps you build a solid foundation in Japanese communication, grammar, vocabulary, and reading/listening skills. This is the entry level for Japanese language proficiency, focusing on basic phrases, simple conversations, and understanding everyday situations.",
    },
    N4: {
      description:
        "The N4 course builds upon N5 knowledge and helps you understand basic Japanese used in everyday situations. You'll learn to comprehend basic conversations, read simple passages on familiar topics, and express yourself in various daily contexts with more confidence.",
    },
    JLPT: {
      description:
        "The Japanese Language Proficiency Test (JLPT) courses prepare you for all levels of certification (N5-N1). Our comprehensive program covers reading, listening, grammar, and vocabulary needed to pass these internationally recognized exams that are often required for academic and professional advancement in Japan.",
    },
    JFT: {
      description:
        "The Japan Foundation Test for Basic Japanese (JFT-Basic) course focuses on practical Japanese communication skills needed for working in Japan. This test is specifically designed for those seeking employment in Japan under various work visa categories, with emphasis on workplace communication and practical language use.",
    },
  }

  // Skill requirements data - sorted to match the filter order
  const skillRequirements = [
    {
      field: "Food Service",
      id: "skill-food-service",
      languageRequirement: {
        exam: "JFT-Basic or JLPT",
        level: "JFT-Basic score of 200+ or JLPT N4",
        percentage: 60, // For progress bar
      },
      otherRequirements: [
        "Food hygiene certification",
        "Basic culinary skills",
        "Customer service experience preferred",
        "Physical stamina",
      ],
      workingConditions: [
        "Shift work including evenings and weekends",
        "Standing for long periods",
        "Fast-paced environment",
        "Team-based work",
      ],
    },
    {
      field: "Agriculture",
      id: "skill-agriculture",
      languageRequirement: {
        exam: "JFT-Basic or JLPT",
        level: "JFT-Basic score of 200+ or JLPT N4",
        percentage: 60,
      },
      otherRequirements: [
        "Physical fitness",
        "Ability to work outdoors in various weather conditions",
        "Basic understanding of agricultural practices",
        "Willingness to learn machinery operation",
      ],
      workingConditions: [
        "Outdoor work in all seasons",
        "Early morning starts",
        "Physical labor",
        "Seasonal work patterns",
      ],
    },
    {
      field: "Care Giving",
      id: "skill-care-giving",
      languageRequirement: {
        exam: "JFT-Basic or JLPT",
        level: "JFT-Basic score of 250+ or JLPT N3",
        percentage: 75,
      },
      otherRequirements: [
        "Certified care worker qualification",
        "First aid certification",
        "Empathetic personality",
        "Physical stamina",
        "Patient handling skills",
      ],
      workingConditions: [
        "Shift work including nights",
        "Emotional challenges",
        "Physical assistance to patients",
        "Detailed record keeping",
      ],
    },
    {
      field: "Building Cleaning",
      id: "skill-building-cleaning",
      languageRequirement: {
        exam: "JFT-Basic or JLPT",
        level: "JFT-Basic score of 200+ or JLPT N4",
        percentage: 60,
      },
      otherRequirements: [
        "Knowledge of cleaning chemicals and equipment",
        "Attention to detail",
        "Physical stamina",
        "Ability to follow safety protocols",
      ],
      workingConditions: [
        "Early morning or evening shifts",
        "Physical work",
        "Working with cleaning chemicals",
        "Independent work in assigned areas",
      ],
    },
    {
      field: "Ground Handling",
      id: "skill-ground-handling",
      languageRequirement: {
        exam: "JLPT",
        level: "JLPT N3 or higher",
        percentage: 80,
      },
      otherRequirements: [
        "Customer service experience",
        "Basic computer skills",
        "Physical fitness",
        "Ability to work under pressure",
        "Safety consciousness",
      ],
      workingConditions: [
        "Shift work including weekends and holidays",
        "Outdoor work in various weather conditions",
        "Standing for long periods",
        "Fast-paced environment",
      ],
    },
    {
      field: "IT Industry",
      id: "skill-it-industry",
      languageRequirement: {
        exam: "JLPT",
        level: "JLPT N2 or higher",
        percentage: 90,
      },
      otherRequirements: [
        "Programming skills (Java, Python, etc.)",
        "Database knowledge",
        "Project management experience",
        "Technical English proficiency",
        "Relevant IT certifications",
      ],
      workingConditions: [
        "Office environment",
        "Project deadlines",
        "Team collaboration",
        "Continuous learning required",
      ],
    },
  ]

  // Trust features data
  const trustFeatures = [
    {
      icon: images.certificate,
      title: "Government Recognized Certification",
      description:
        "Our programs are officially recognized by both Japanese and local government authorities, ensuring your qualifications are valid for employment in Japan.",
    },
    {
      icon: images.handshake,
      title: "Direct Employer Partnerships",
      description:
        "We maintain strong relationships with over 150 Japanese employers across various industries, providing direct placement opportunities for our graduates.",
    },
    {
      icon: images.support,
      title: "Comprehensive Support System",
      description:
        "From visa application to accommodation arrangements, our dedicated team provides end-to-end support throughout your journey to employment in Japan.",
    },
    {
      icon: images.curriculum,
      title: "Industry-Aligned Curriculum",
      description:
        "Our courses are designed in collaboration with Japanese employers to ensure you learn exactly what's needed to succeed in your chosen field.",
    },
  ]

  // Trust badges data
  const trustBadges = [
    {
      icon: images.verified,
      text: "Government Certified",
    },
    {
      icon: images.award,
      text: "Award-Winning Program",
    },
    {
      icon: images.guarantee,
      text: "Job Placement Guarantee",
    },
  ]

  // Course attributes
  const courseAttributes = [
    { icon: images.icon1, title: "Exam Preparation", desc: "Comprehensive JLPT training." },
    { icon: images.icon2, title: "JPT Coaching", desc: "Improve language and job readiness." },
    { icon: images.icon3, title: "Career Guidance", desc: "Update your competitive skills." },
    { icon: images.icon4, title: "Job Interview Preparation", desc: "Succeed in interviews." },
  ]

  // Handle Learn More button click
  const handleLearnMoreClick = (course) => {
    // Set the active tab if it's a language course
    if (course.type === "Language" && course.targetTab) {
      setActiveTab(course.targetTab)
    } else if (course.type === "Skill" && course.targetTab) {
      setActiveSkillTab(course.targetTab)
    }

    // Scroll to the target section
    scrollToSection(course.targetSection)
  }

  // Number counter animation
  const animateValue = (obj, start, end, duration) => {
    let startTimestamp = null
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      const value = Math.floor(progress * (end - start) + start)
      obj.innerHTML = value.toLocaleString()
      if (progress < 1) {
        window.requestAnimationFrame(step)
      }
    }
    window.requestAnimationFrame(step)
  }

  // Scroll animation effect
  useEffect(() => {
    // Select all elements that should be animated
    const animatedElements = document.querySelectorAll(
      ".animate-on-scroll, .animate-fade-in-left, .animate-fade-in-right, .animate-scale-up, .skill-requirement-card, .detailed-skill-courses-header, .language-requirement, .trust-feature, .trust-image, .trust-header",
    )

    // Create intersection observer to detect when elements enter viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")

            // Start counting animation for stat numbers
            if (entry.target.classList.contains("stat-card") && !countingStarted) {
              const statNumber = entry.target.querySelector(".stat-number")
              if (statNumber) {
                const finalValue = Number.parseInt(statNumber.getAttribute("data-value"))
                animateValue(statNumber, 0, finalValue, 2000)
              }
              setCountingStarted(true)
            }

            // Optional: unobserve after visible to improve performance
            observer.unobserve(entry.target)
          }
        })
      },
      {
        threshold: 0.1, // Trigger when at least 10% of the element is visible
      },
    )

    // Observe all animated elements
    animatedElements.forEach((el) => observer.observe(el))

    // Cleanup function to remove observers when component unmounts
    return () => {
      animatedElements.forEach((el) => observer.unobserve(el))
    }
  }, [selectedFilter, activeTab, countingStarted])

  return (
    <div className="academic">
      {/* Course Filter */}
      <div className="courses-section animate-on-scroll">

        {/* Added Title */}
        <div style={{ textAlign: 'center', marginBottom: '40px', paddingTop: '40px' }}>
          <h2 className="heading-md" style={{ color: 'var(--color-charcoal)' }}>{t("academic_title")}</h2>
          <p className="text-body" style={{ maxWidth: '600px', margin: '0 auto' }}>{t("academic_subtitle")}</p>
        </div>

        {/* Search bar */}
        <form className="search-container animate-fade-in-left" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            className="search-input"
            placeholder={t('search_placeholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button type="submit" className="search-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
          </button>
        </form>

        {/* Filter buttons */}
        <div className="filter-buttons animate-scale-up">
          <button className={selectedFilter === "Skill" ? "active" : ""} onClick={() => setSelectedFilter("Skill")}>
            {t('filter_skill')}
          </button>
          <button
            className={selectedFilter === "Language" ? "active" : ""}
            onClick={() => setSelectedFilter("Language")}
          >
            {t('filter_language')}
          </button>
        </div>

        {/* Course Cards - Centered */}
        <div className="courses-list" style={{ justifyContent: 'center' }}>
          {filteredCourses.map((course) => (
            <div key={course.id} className="course-item animate-fade-in-left">
              <h3>{course.title}</h3>
              <img src={course.image || "/placeholder.svg"} alt={course.title} className="course-image" />
              <p className="course-description">{course.description}</p>

              <div className="course-details">
                <div className="detail-item">
                  <span className="detail-label">{t('duration')}:</span>
                  <span className="detail-value">{course.duration}</span>
                </div>
              </div>

              <div className="course-button-container">
                <button
                  className="course-button"
                  onClick={() => handleLearnMoreClick(course)}
                  data-course-title={course.title}
                >
                  {t('btn_learn_more')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Details Section */}
      <section className="detailed-course animate-scale-up">
        <div className="detailed-course-container">
          <div className="detailed-course-header animate-fade-in-left">
            <h2>{t("aca1")}{/*What to Expect from Our Courses*/}</h2>
            <p>{t("aca2")}{/*Enhance your language skills for better career opportunities in Japan.*/}</p>
          </div>

          <div className="detailed-course-content animate-fade-in-right">
            <div className="image-card">
              <img src={images.courseDetail || "/placeholder.svg"} alt="JPT Course" />
            </div>

            <div className="attr-container">
              {courseAttributes.map((attr, i) => (
                <div key={i} className="attr animate-fade-in-left">
                  <div className="attr-icon">
                    <img src={attr.icon || "/placeholder.svg"} alt={attr.title} />
                  </div>
                  <h3>{attr.title}</h3>
                  <p>{attr.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Skill Courses Section */}
      <section id="detailed-skill-courses" className="detailed-skill-courses">
        <div className="detailed-skill-courses-header animate-on-scroll">
          <h2>{t("aca3")}{/*Japan Employment Requirements*/}</h2>
          <p>
            Each field of work in Japan has specific language proficiency requirements and other qualifications. Below
            are the key requirements for various industries where foreign workers are in demand.
          </p>
        </div>

        {/* Updated skill tabs container with same design as language tabs */}
        <div className="skill-tabs-container">

          <div className="skill-tabs" ref={skillTabsRef}>
            {skillRequirements.map((skill) => (
              <button
                key={skill.id}
                className={`skill-tab-button ${activeSkillTab === skill.field ? "active" : ""}`}
                onClick={() => handleSkillTabClick(skill.field)}
              >
                {skill.field}
              </button>
            ))}
          </div>

        </div>

        <div className="skill-tab-content animate-scale-up">
          {skillRequirements.map((skill) => (
            <div key={skill.id} className={`skill-tab-pane ${activeSkillTab === skill.field ? "active" : ""}`}>
              <div className="requirements-columns">
                <div className="requirement-section">
                  <h4>{t('requirements')}</h4>
                  <ul>
                    {skill.otherRequirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>

                <div className="requirement-section">
                  <h4>{t('working_conditions')}</h4>
                  <ul>
                    {skill.workingConditions.map((condition, i) => (
                      <li key={i}>{condition}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="language-requirement animate-fade-in-right">
                <h4>{t('language_proficiency')}</h4>
                <p>{t('required_exam')}: {skill.languageRequirement.exam}</p>
                <p>{t('minimum_level')}: {skill.languageRequirement.level}</p>

                <div className="language-level">
                  <span className="language-level-label">{t('proficiency')}:</span>
                  <div className="language-level-bar">
                    <div
                      className="language-level-fill"
                      style={{ width: `${skill.languageRequirement.percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>




      {/* New Trust Building Section */}
      <section id="trust-section" className="trust-section">
        <div className="trust-container">
          <div className="trust-header animate-on-scroll">
            <h2>{t("aca4")}{/*Why Choose Our Vocational Center*/}</h2>
            <p>{t("aca5")}{/*}
              With over a decade of experience in preparing candidates for successful careers in Japan, we provide
              comprehensive training and guaranteed job placement to make your Japan employment dream a reality.
            */}</p>
          </div>

          <div className="trust-content">
            <div className="trust-image animate-fade-in-left">
              <img src={images.japanSuccess || "/placeholder.svg"} alt="Successful graduates working in Japan" />
            </div>

            <div className="trust-features">
              {trustFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="trust-feature animate-fade-in-right"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  <div className="trust-feature-icon">
                    <img src={feature.icon || "/placeholder.svg"} alt={feature.title} />
                  </div>
                  <div className="trust-feature-content">
                    <h3 className="trust-feature-title">{feature.title}</h3>
                    <p className="trust-feature-description">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="trust-badges">
            {trustBadges.map((badge, index) => (
              <div
                key={index}
                className="trust-badge animate-fade-in-left"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <img src={badge.icon || "/placeholder.svg"} alt={badge.text} className="trust-badge-icon" />
                <span className="trust-badge-text">{badge.text}</span>
              </div>
            ))}
          </div>

          <div className="trust-cta animate-scale-up">
            <a href="academic" className="trust-cta-button animate-pulse">
              {t('academic_cta')}
            </a>
          </div>
        </div>
      </section>






      {/* Language Tabs Section */}
      <section id="language-tabs-section" className="language-tabs-section">
        <div className="section-header animate-on-scroll">
          <h2>{t('academic_lang_title')}</h2>
          <p>
            {t('academic_lang_desc')}
          </p>
        </div>

        <div className="tabs-container animate-fade-in-left">

          <div className="tabs animate-fade-in-right" ref={languageTabsRef}>
            {Object.keys(languageTabContent).map((tab) => (
              <button
                key={tab}
                className={`tab-button ${activeTab === tab ? "active" : ""}`}
                onClick={() => handleTabClick(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="tab-content animate-scale-up">
            {Object.keys(languageTabContent).map((tab) => (
              <div key={tab} className={`tab-pane ${activeTab === tab ? "active" : ""}`}>
                <p>{languageTabContent[tab].description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


    </div>
  )
}

export default Academics
